import { Col, Dropdown, Modal, Row, Space, Switch, Typography, message } from "antd";
import ManagersView from "../components/ManagersView";
import { Guid } from "guid-typescript";
import { useState } from "react";
import RegisterUsersWizard from "../components/RegisterUsersWizard";
import { SearchQuery } from "../code/search-query/search-query";
import Loader from "../controls/Loader";
import {
  getArchivedManagers,
  getCompanyManagersExport,
  restoreManager,
} from "../repository/data.repository";
import ArchivedListView from "../controls/ArchivedListView";
import { ManagerUser } from "../repository/data.default";
import ManagerView from "../components/ManagerView";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";
import { getProblemMessage } from "../utils/http";

function ManagersPage() {
  const [modal, contextHolder] = Modal.useModal();
  const [registerIsOpen, setRegisterIsOpen] = useState(false);
  const [triggerView, setTriggerView] = useState(Guid.create().toString());
  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    conditions: [],
  });
  const [userInputMode, setUserInputMode] = useState<"delete" | "view">(
    "delete",
  );
  const [userInputUid, setUserInputUid] = useState<string | null | undefined>(
    {} as any,
  );
  const [userInputOpen, setUserInputOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const permissions = useSelector(authorizeSelectors.permissions);

  // Events

  function onSearch(searchQuery: SearchQuery): void {
    setSearchQuery(searchQuery);
  }

  function onManagerInputOpen(mode: "view", values?: ManagerUser): void {
    setUserInputMode(mode);
    setUserInputOpen(true);
    setUserInputUid(values?.uid);
  }

  function onDownloadExport(): void {
    const loading = modal.info({
      content: (
        <Typography.Text className="m-1 ml-2 inline-block">
          Downloading managers export file..
        </Typography.Text>
      ),
      footer: null,
      centered: true,
      icon: <Loader />,
    });
    getCompanyManagersExport({
      searchQuery,
      pageNumber: 0,
      pageSize: Number.MAX_SAFE_INTEGER,
    })
      .then(() => {
        loading.destroy();
      })
      .catch((err) => {
        loading.destroy();
        message.error(getProblemMessage(err) || "Failed to download export.");
      });
  }

  function onRegisterUserOpen(): void {
    setRegisterIsOpen(true);
  }

  function onRegisterUserCancel(): void {
    setRegisterIsOpen(false);
  }

  function onRegisterUserFinish(): void {
    setTriggerView(Guid.create().toString());
  }

  function onUserFinish(): void {
    setUserInputOpen(false);
    setTriggerView(Guid.create().toString());
  }

  function onUserClose(): void {
    setUserInputOpen(false);
  }

  return (
    <Col className="relative flex flex-auto flex-col overflow-hidden overflow-y-auto">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4">
        <Typography.Title className="m-0 flex-auto" level={4}>
          Managers
        </Typography.Title>
        <Space className="flex flex-row">
          <Typography.Text type="secondary">Show archived</Typography.Text>
          <Switch
            size="small"
            checked={showArchived}
            onChange={setShowArchived}
          />
          <Dropdown.Button
            type="primary"
            disabled={!permissions.can("CompanyManagers", "create")}
            menu={{
              items: [
                {
                  key: "downloadExport",
                  label: "Export to Excel",
                  disabled: !permissions.can("CompanyManagers", "view"),
                  onClick: onDownloadExport,
                },
              ],
            }}
            onClick={onRegisterUserOpen}
          >
            Register Managers
          </Dropdown.Button>
        </Space>
      </Row>
      <Col className="flex flex-auto flex-col overflow-hidden p-4 pb-4 pt-0">
        {showArchived ? (
          <ArchivedListView<ManagerUser>
            entityLabel="manager"
            loader={getArchivedManagers as any}
            restore={restoreManager}
            rowKey={(row) => row.uid}
            columns={[
              { title: "User Name", dataIndex: "userName", width: 180 },
              { title: "First Name", dataIndex: "firstName", width: 160 },
              { title: "Last Name", dataIndex: "lastName", width: 160 },
              { title: "Email", dataIndex: "email", width: 220 },
              {
                title: "Deactivated",
                dataIndex: "deactivatedAt",
                width: 160,
                render: (v?: string | null) =>
                  v ? new Date(v).toLocaleString("en-GB") : "—",
              },
            ]}
          />
        ) : (
          <ManagersView
            trigger={triggerView}
            onSearch={onSearch}
            onAction={(key: string, record: any) =>
              onManagerInputOpen(key as any, record)
            }
          />
        )}
      </Col>
      <ManagerView
        open={userInputOpen}
        mode={userInputMode}
        uid={userInputUid!}
        onFinish={onUserFinish}
        onClose={onUserClose}
      />
      <RegisterUsersWizard
        open={registerIsOpen}
        onFinish={onRegisterUserFinish}
        onCancel={onRegisterUserCancel}
      />
      {contextHolder}
    </Col>
  );
}

export default ManagersPage;
